import React, { useRef, useEffect, forwardRef, useState } from 'react';
import { sortBy, isEqual } from 'lodash';
import { Input, MenuList, MenuItem, Chip, Box } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import useAutocomplete from '@mui/material/useAutocomplete';
import RemoveAllIcon from './RemoveAllIcon';
import Asterisk from '../../shared/components/Asterisk';
import SkeletonWrapper from './skeleton/SkeletonWrapper';
import cx from 'classnames';

/**
 * @typedef {object} SelectionListProps
 * @property {boolean} [allowAutocomplete]
 * @property {boolean} [disabled]
 * @property {string} [id]
 * @property {boolean} [showInputEndAdornment]
 * @property {React.ReactNode} [label]
 * @property {Array<*>} [list]
 * @property {(selectedValue: { label: string, value: number | string } | Array<{ label: string, value: number | string }> | null) => void} [onSelectHandler]
 * @property {string} [placeholder]
 * @property {*|Array<*>} [selectedValue]
 * @property {boolean} [multiSelect]
 * @property {boolean} [addSelectAll]
 * @property {boolean} [required]
 * @property {boolean} [loading]
 * @property {boolean} [hasError]
 * @property {boolean} [allowDeselectSingleSelect]
 */

/**
 * @deprecated This component is obsolete and should not be used in new code, please use OpenSelectionList.
 * @type {React.ForwardRefExoticComponent<SelectionListProps & React.RefAttributes<*>>}
 * @param {SelectionListProps} props
 */
const SelectionList = forwardRef((props, ref) => {
  const {
    id,
    onSelectHandler,
    placeholder,
    label = '',
    allowAutocomplete = true,
    disabled = false,
    showInputEndAdornment = true,
    list = [],
    selectedValue = null,
    multiSelect = false,
    addSelectAll = false,
    required = false,
    loading = false,
    hasError = false,
    allowDeselectSingleSelect = false
  } = props;

  const [inputValue, setInputValue] = useState(
    !multiSelect && selectedValue ? selectedValue.label : ''
  );

  const pageMode = multiSelect ? PAGE_MODE.MULTI_SELECTION : PAGE_MODE.SINGLE_SELECTION;
  const settings = pageSettings.get(pageMode);

  const menuListRef = useRef(null);

  useEffect(() => {
    if (!multiSelect || !disabled) {
      return;
    }

    if (!menuListRef || !menuListRef.current) {
      return;
    }

    menuListRef.current.scrollTop = 0;
  }, [multiSelect, disabled, selectedValue]);

  const isAllSelected = addSelectAll && list?.length === selectedValue?.length;

  const showRemoveAllIcon =
    !disabled &&
    (multiSelect || allowDeselectSingleSelect) &&
    settings.isAnyOptionSelected(selectedValue);

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    value,
    setAnchorEl
  } = useAutocomplete({
    getOptionLabel: o =>
      o && multiSelect && o?.length > 0 ? o.map(x => x.label).join(',') : o?.label,
    isOptionEqualToValue: (option, value) => option.value === value?.value,
    onChange: (_, value, reason) => {
      if (allowDeselectSingleSelect && isEqual(value, selectedValue)) {
        return removeAllItems();
      }
      return reason !== 'clear' && selectedValueChangeHandler(value);
    },
    open: !loading,
    options: list,
    selectOnFocus: false,
    value: settings.isAnyOptionSelected(selectedValue) ? selectedValue : settings.emptyValue,
    multiple: multiSelect,
    id,
    inputValue
  });

  const selectedValueChangeHandler = value => {
    onSelectHandler(settings.getSelectedValue(value));
    setInputValue(!multiSelect && value ? value.label : '');
  };

  const selectAllItems = () => {
    if (isAllSelected) {
      return;
    }

    onSelectHandler([...list]);
  };

  const removeAllItems = () => {
    onSelectHandler(settings.emptyValue);
    setInputValue('');
  };

  return (
    <div {...getRootProps()}>
      {label && label !== '' && (
        <span {...getInputLabelProps()} className='mui-selection-list__label'>
          {label}
          {required ? <Asterisk /> : ''}
        </span>
      )}

      <div
        data-testid={`${id}-wrapper`}
        className={cx('mui-selection-list__choices-wrapper', { hasError: hasError })}>
        <div className='input-wrapper' ref={setAnchorEl}>
          <Input
            className={cx({
              'mui-selection-list__hover-pointer': !allowAutocomplete && !disabled
            })}
            inputRef={ref}
            data-testid={`${id}-input`}
            disableUnderline
            placeholder={settings.isAnyOptionSelected(value) ? '' : placeholder}
            disabled={disabled}
            readOnly={!allowAutocomplete}
            inputProps={{
              ...getInputProps(),
              className: cx({
                'mui-selection-list__hover-pointer': !allowAutocomplete && !disabled
              })
            }}
            onChange={event => setInputValue(event.target.value)}
          />
          {multiSelect && (
            <Box
              className={cx('selected-items')}
              sx={{ display: 'flex', overflow: 'auto', gap: 0.5 }}>
              {value &&
                value.map((element, index) => (
                  <div key={index}>
                    <Chip
                      disabled={disabled}
                      label={element.displayName ? element.displayName : element.label}
                      onDelete={() => {
                        selectedValueChangeHandler(
                          selectedValue.filter(x => x.value != element.value)
                        );
                      }}
                    />
                  </div>
                ))}
            </Box>
          )}
          {showInputEndAdornment && (
            <SearchIcon className='mui-selection-list__choice-adornment icon' />
          )}
          {showRemoveAllIcon && (
            <RemoveAllIcon className='remove-all-icon icon' removeAllHandler={removeAllItems} />
          )}
        </div>
        <SkeletonWrapper show={loading} inheritParentSize={true}>
          {list?.length ? (
            <MenuList
              {...getListboxProps()}
              ref={menuListRef}
              className='mui-selection-list__choices'
              data-testid={`${id}-list`}
              id={`${id}-list`}>
              {addSelectAll && (
                <MenuItem
                  role='option'
                  disabled={disabled}
                  key='SelectAll'
                  className={cx('mui-selection-list__choices__item', {
                    'mui-selection-list__choices__item-selected': isAllSelected
                  })}
                  onClick={selectAllItems}>
                  Select All
                </MenuItem>
              )}
              {groupedOptions.map((option, index) => (
                <MenuItem
                  {...getOptionProps({ option, index })}
                  disabled={disabled || option.disabled}
                  key={index}
                  className={cx('mui-selection-list__choices__item', {
                    'mui-selection-list__choices__item-selected': settings.isOptionSelected(
                      selectedValue,
                      option.value
                    )
                  })}
                  value={option.value}>
                  {option.labelRendered || option.label}
                </MenuItem>
              ))}
            </MenuList>
          ) : (
            <MenuList />
          )}
        </SkeletonWrapper>
      </div>
    </div>
  );
});

SelectionList.displayName = 'SelectionList';

const PAGE_MODE = {
  MULTI_SELECTION: 1,
  SINGLE_SELECTION: 2
};

const pageSettings = new Map([
  [
    PAGE_MODE.MULTI_SELECTION,
    {
      isAnyOptionSelected: selectedValue => selectedValue?.length > 0,
      getSelectedValue: value => sortBy(value, v => v.value),
      isOptionSelected: (selectedValue, value) => selectedValue.find(x => x.value == value),
      emptyValue: []
    }
  ],
  [
    PAGE_MODE.SINGLE_SELECTION,
    {
      isAnyOptionSelected: selectedValue => selectedValue != null,
      getSelectedValue: value => value,
      isOptionSelected: (selectedValue, value) => selectedValue && value === selectedValue.value,
      emptyValue: null
    }
  ]
]);

export default SelectionList;
